// Grilla de las imágenes ya generadas de la empresa, para elegir CUÁL usar como
// punto de partida. La comparten los modos que arrancan de una imagen: "Animar
// imagen" (→ video) y "Carrusel" (→ variaciones). Se monta inline en desktop
// (debajo del hero, como un historial) y dentro del bottom sheet en mobile — una
// sola implementación para los dos.
//
// Diferencias con GeneratedImagesPicker (el del asistente): excluye los videos (de
// un video no se parte) y devuelve el `id` de la fila, que es lo que el backend
// necesita. Ver ADR-2026-08-14-modo-animar-imagen y ADR-2026-08-14-modo-carrusel.
//
// La selección es ÚNICA por defecto (`selectedId`) porque así la usan los dos modos
// que animan o varían UNA foto. "Variantes de acabado" elige varias y pasa
// `selectedIds`, que manda cuando está: la grilla sólo dibuja el tilde, y quién la
// usa decide si `onSelect` agrega, reemplaza o saca
// (ADR-2026-09-04-la-imagen-de-partida-puede-ser-mas-de-una).
//
// `tileInicial` mete celdas propias ADELANTE de las creaciones, en la misma grilla: es
// donde "Variantes de acabado" pone su card de "Subir imagen" y las fotos que el
// usuario subió (ADR-2026-09-04-subir-imagen-es-la-primera-celda-de-la-grilla). Van
// también en el skeleton y en el vacío, que es cuando más se las necesita.
//
// `conFiltroProducto` suma el desplegable de producto que ya tiene Creaciones
// (ADR-2026-09-04-filtro-por-producto-en-creaciones): las creaciones de una empresa
// se leen como carpetas por producto, y "Variantes de acabado" quiere poder partir de
// TODAS las de un producto (ADR-2026-09-04-elegir-el-set-de-un-producto). Va detrás de
// una prop porque los otros dos modos eligen UNA foto y el filtro no les suma nada.
//
// `brandId` acota a UNA marca, un escalón más abajo que `rubro`
// (ADR-2026-09-10-las-creaciones-del-paso-1-son-de-la-marca-activa) — hoy lo pide sólo
// "Mostrar cómo funciona" de Belleza; Animar, Carrusel y los atajos siguen sin filtro.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { orDeMarca } from '../../../../utils/filtroDeMarca';
import CachedImg from '../../../../components/CachedImg';
import { firmarEnLote } from '../../../../utils/firmarEnLote';
import { ProductGridSkeleton } from '../../../../components/ProductGridSkeleton';

const GENERADAS_BUCKET = 'generadas';
const LIMIT = 60;

/**
 * Cuántas celdas se DIBUJAN por página; el resto espera a que el usuario baje.
 *
 * La grilla traía las 60 de una: una consulta, y después **60 pedidos de firma**, uno por
 * archivo, antes de poder dibujar nada. El panel se quedaba en el esqueleto hasta que
 * volvían todos y recién ahí aparecían las 60 juntas. Es el mismo problema que ya
 * resolvió la columna de resultados, y se copia su arreglo: firmar EN LOTE
 * (ADR-2026-09-08-las-creaciones-del-panel-se-firman-en-lote) y dibujar por páginas con
 * un centinela al final (ADR-2026-09-04-las-creaciones-del-panel-se-cargan-por-tanda).
 *
 * 12, como allá: llena varias filas en las dos grillas (2 columnas en mobile, 3 en
 * desktop), así que con menos cualquier scroll corto pediría la siguiente enseguida.
 */
const CELDAS_POR_PAGINA = 12;

/**
 * Cuántas veces se reintenta un lote de firmas que volvió ENTERO vacío, y cuánto se
 * espera entre intentos. Dos alcanzan para un hipo de red; más que eso ya no es un hipo.
 */
const MAX_REINTENTOS_FIRMA = 2;
const ESPERA_REINTENTO_MS = 1500;
// Sólo para armar la lista de productos: no firma URLs ni dibuja nada, así que puede
// mirar mucho más atrás que la grilla.
const LIMITE_PRODUCTOS = 1000;

// 2 columnas en mobile, 3 de `md` para arriba —
// ADR-2026-08-20-la-grilla-de-elegir-imagen-va-en-dos-columnas-en-mobile.
//
// Eran 3 fijas, con el argumento de que la grilla vive DENTRO de un panel angosto (la
// card de opciones en desktop, el bottom sheet en mobile) y por eso los breakpoints de
// viewport no aplicaban. Vale para no irse a `lg:grid-cols-5` —ahí los tiles quedaban
// minúsculos—, pero no para el corte mobile/desktop: los DOS montajes de esta grilla
// están separados por `md` en los tres modos que la usan (el sheet sólo se abre desde la
// rama mobile, la card inline vive dentro de un `hidden md:flex`). O sea que acá el
// breakpoint de viewport SÍ coincide con el ancho real del contenedor.
//
// En un teléfono, 3 tiles por fila dejaban miniaturas de ~100px: hay que adivinar cuál
// es cuál para elegir. Con 2 se ven.
const GRID_CLASS = 'grid grid-cols-2 gap-3 md:grid-cols-3';

export interface GeneradaPick {
  id: string;
  signedUrl: string;
  /** De qué producto es la creación —la fila de `variantes`—, si tiene uno. */
  varianteId: string | null;
  /** El nombre de ese producto, para poder decirlo sin volver a consultar. */
  producto: string | null;
  /**
   * El `file_name` de la fila. Lo usa quien tiene que nombrar una descarga —hoy el
   * recorte, que baja `<nombre>_recortada.png`
   * (ADR-2026-09-07-el-recorte-se-hace-en-el-panel-del-modo)—. La grilla ya lo traía de
   * la base para armar la URL firmada; devolverlo evita que el consumidor vuelva a
   * consultarlo por el id.
   */
  fileName: string;
}

/**
 * Una fila tal como vuelve de la consulta: todavía SIN firmar. La URL se agrega cuando la
 * celda entra en una página (ver `CELDAS_POR_PAGINA`).
 */
interface FilaGenerada {
  id: string;
  path: string;
  varianteId: string | null;
  producto: string | null;
  fileName: string;
}

/** Un producto con creaciones: lo que lista el desplegable. */
interface ProductoConCreaciones {
  id: string;
  nombre: string;
}

/** Las marcas de la empresa que son del rubro activo. */
interface MarcasDelRubro {
  ids: Set<string>;
  /**
   * La empresa tiene marcas de OTROS rubros.
   *
   * Es lo que decide si hay algo que filtrar: en una empresa que sólo vende muebles
   * —la enorme mayoría— todas sus creaciones son del rubro, y filtrar sería pagar una
   * consulta más grande para no descartar nada.
   */
  hayOtras: boolean;
}

const TODOS = '__todos__';

export const GeneradasPickGrid: React.FC<{
  companyId: string | null;
  selectedId: string | null;
  /** Selección MÚLTIPLE: cuando viene, reemplaza a `selectedId` para el tilde. */
  selectedIds?: string[];
  onSelect: (pick: GeneradaPick) => void;
  emptyHint?: string;
  /**
   * Una celda que va PRIMERA en la grilla, antes de las generadas — el "Subir imagen"
   * del modo Animar con el diseño nuevo
   * (ADR-2026-09-07-animar-imagen-toma-el-diseno-de-los-modos), y las celdas propias de
   * los modos de Hogar y Decoración.
   *
   * Va adentro de la grilla y no arriba de ella porque es una opción MÁS de la misma
   * elección: "de dónde sale la imagen". Es el mismo lugar que ocupa "Subir nuevo
   * producto" en la grilla de productos de los otros modos.
   *
   * Se dibuja también mientras carga y cuando no hay ninguna generada: es justo cuando
   * subir es la única salida, así que es cuando menos puede faltar.
   */
  tileInicial?: React.ReactNode;
  /** El desplegable de producto arriba de la grilla, como en Creaciones. */
  conFiltroProducto?: boolean;
  /**
   * Acota la grilla a UN producto, sin desplegable ni forma de salir.
   *
   * Para cuando el producto no lo elige el usuario sino el contexto: en "Se transforma",
   * la foto de cómo TERMINA tiene que ser del mismo mueble que la de cómo empieza, así
   * que ahí sólo se ofrecen las creaciones de ese producto
   * (ADR-2026-09-04-como-termina-se-elige-de-las-creaciones-del-producto). `null` o
   * `undefined` no acota nada.
   */
  productoFijo?: string | null;
  /**
   * Rubro de la marca activa: la grilla y el desplegable muestran SÓLO lo de ese rubro.
   *
   * Una misma empresa tiene una marca de deco y otra de indumentaria, y sus productos
   * conviven en `variantes`: sin esto, un modo de muebles ofrecía "Buzo" y "Campera"
   * (ADR-2026-09-04-el-desplegable-de-producto-se-acota-al-rubro). `undefined` o `null`
   * —rubro todavía sin resolver, o marca sin rubro cargado— no filtra nada.
   *
   * Al principio acotaba sólo el desplegable y las FOTOS seguían siendo las de toda la
   * empresa: el modo de muebles se abría con las últimas remeras generadas a la vista
   * (ADR-2026-09-10-las-creaciones-del-paso-1-se-acotan-al-rubro). Ahora acota las dos
   * cosas: quedan las creaciones cuyo producto es de una marca del rubro. Las que no
   * tienen producto no se pueden atribuir a ninguna marca y quedan afuera —a esas se
   * llega subiendo la foto—; los productos SIN marca siguen en el desplegable, y
   * elegirlos muestra sus creaciones, porque con un producto elegido no se filtra.
   */
  rubro?: string | null;
  /**
   * Elegir (o soltar) DE UNA TODAS las creaciones que están a la vista.
   *
   * Es lo que convierte al filtro en "elegir el set del producto": sin esto, partir de
   * las nueve fotos de un mueble son nueve toques. Sólo se dibuja cuando viene.
   */
  onElegirVarias?: (picks: GeneradaPick[], elegir: boolean) => void;
  /**
   * Acota la grilla a las creaciones de UNA marca —
   * ADR-2026-09-10-las-creaciones-del-paso-1-son-de-la-marca-activa. Es el mismo recorte
   * que `rubro` pero un escalón más abajo, y por eso manda sobre él cuando viene: el
   * rubro deja pasar todas las marcas del rubro, y acá se quiere una sola.
   *
   * La marca es la columna `brand_id` de la fila (antes se deducía por el producto con
   * dos `!inner`; ver ADR-2026-09-13-brand-id-en-imagenes-generadas). "De esta marca"
   * es `brand_id = X` **o sin marca**: lo que no se puede atribuir a ninguna —94k filas
   * históricas sin producto o con producto huérfano— se ve desde todas, como los
   * productos huérfanos (ADR-2026-09-13-las-generaciones-sin-marca-se-ven-en-todas).
   * Las generaciones nuevas ya traen la marca activa, así que ese conjunto no crece.
   *
   * Tres valores, y los tres significan algo distinto:
   *   • ausente / `undefined` → sin filtro por marca. Es lo que tienen hoy Animar y
   *     Carrusel, y no cambia.
   *   • un `id` → sólo las de esa marca.
   *   • `null` → la marca TODAVÍA no se resolvió: la grilla se queda en su esqueleto en
   *     vez de listar las de toda la empresa y cambiarlas medio segundo después, debajo
   *     del cursor. Quien pase esta prop tiene que pasar `undefined` (y no `null`) si lo
   *     que quiere es no filtrar.
   */
  brandId?: string | null;
}> = ({
  companyId,
  selectedId,
  selectedIds,
  onSelect,
  emptyHint,
  tileInicial,
  conFiltroProducto = false,
  productoFijo,
  rubro,
  onElegirVarias,
  brandId,
}) => {
  const [loading, setLoading] = useState(true);
  /**
   * Las filas de la consulta, SIN firmar. La firma es lo caro y se hace por página; acá
   * vive lo que la base devolvió, que es lo que decide cuántas celdas hay en total.
   */
  const [filas, setFilas] = useState<FilaGenerada[]>([]);
  /** `path → URL firmada` de lo que ya se firmó. Crece de a una página. */
  const [urls, setUrls] = useState<Map<string, string>>(() => new Map());
  /**
   * Los paths que Storage NO pudo firmar (el objeto no existe, la fila quedó huérfana).
   *
   * Sin esto, una foto que no firma se quedaba contando como "esperando": su celda
   * mostraba el esqueleto PARA SIEMPRE, porque nunca iba a llegar una URL. Y peor, seguía
   * sumando al total de "Elegir las N" aunque no se pudiera elegir. Marcadas acá, salen
   * de la lista: la grilla no deja un hueco cargando y el número del botón dice lo que de
   * verdad se puede elegir (ADR-2026-09-25-revision-greptile-la-grilla-de-elegir-imagen).
   */
  const [fallidas, setFallidas] = useState<Set<string>>(() => new Set());
  /** Cuántas celdas se dibujan ahora. Vuelve a la primera página al cambiar de lista. */
  const [visibles, setVisibles] = useState(CELDAS_POR_PAGINA);
  /**
   * Contador de reintentos del lote de firmas.
   *
   * `firmarEnLote` no distingue "este objeto no existe" de "se cayó el pedido": cuando
   * `createSignedUrls` falla, `data` viene null y el mapa vuelve VACÍO, igual que si
   * ninguno de los paths existiera. Dar por muertas las doce de una página por un
   * hipo de red vaciaba el panel hasta recargar, así que cuando no vuelve NINGUNA se
   * reintenta en vez de marcarlas (ADR-2026-09-25-revision-greptile-un-lote-vacio-no-mata-la-pagina).
   */
  const [reintentos, setReintentos] = useState(0);
  /** Le pasaron `brandId` y todavía vale `null`: hay que esperar, no listar todo. */
  const esperandoMarca = brandId === null;
  const [marcas, setMarcas] = useState<MarcasDelRubro | null>(null);
  const [productos, setProductos] = useState<ProductoConCreaciones[]>([]);
  const [productoId, setProductoId] = useState<string>(TODOS);
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const filtroRef = useRef<HTMLDivElement>(null);

  /**
   * Las marcas del rubro activo. Lo usan la grilla y el desplegable, así que se resuelve
   * UNA vez y no dentro de cada uno.
   *
   * El producto llega a su rubro por la marca de su producto padre
   * (`variantes → productos.brand_id → brand_profiles.rubro`), que es la misma cadena
   * que usa el filtro por marca de Creaciones.
   *
   * Siempre termina en un valor, aunque la consulta falle: los dos que lo esperan se
   * quedan en el esqueleto hasta que llegue, y sin marcas `hayOtras` es `false`, o sea
   * "no filtres" — ante un error se ve de más, nunca una grilla vacía para siempre.
   */
  useEffect(() => {
    if (!companyId || !rubro) {
      setMarcas(null);
      return;
    }
    let cancelled = false;
    (async () => {
      let lista: any[] = [];
      try {
        const { data } = await supabase
          .from('brand_profiles')
          .select('id, rubro')
          .eq('id_company', companyId);
        lista = (data as any[]) ?? [];
      } catch {
        /* noop — sin marcas no se filtra */
      }
      if (cancelled) return;
      setMarcas({
        ids: new Set(
          lista.filter((m) => m.rubro === rubro).map((m) => String(m.id)),
        ),
        hayOtras: lista.some((m) => m.rubro !== rubro),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, rubro]);

  /**
   * Los productos QUE TIENEN creaciones, para el desplegable.
   *
   * Sale de las imágenes y no de la tabla `variantes`: el desplegable tiene que ofrecer
   * carpetas que existan —un producto del catálogo sin ninguna creación no es una
   * carpeta— y así no hace falta saber nada de marcas acá. Trae sólo `variante_id` y el
   * nombre, sin firmar ninguna URL, así que es barato aunque el tope sea alto.
   */
  useEffect(() => {
    if (!companyId || !conFiltroProducto) return;
    // Con rubro, esperar a saber sus marcas: listar productos y recién después sacarles
    // la mitad deja el desplegable parpadeando.
    if (rubro && !marcas) return;
    let cancelled = false;
    (async () => {
      const marcasDelRubro = marcas?.hayOtras ? marcas.ids : null;
      const { data } = await supabase
        .from('imagenes_generadas')
        .select('variante_id, brand_id, variantes:variantes(nombre)')
        .eq('id_company', companyId)
        .eq('active', true)
        .not('variante_id', 'is', null)
        .order('created_at', { ascending: false })
        .limit(LIMITE_PRODUCTOS);
      if (cancelled || !data) return;
      const porId = new Map<string, string>();
      for (const r of data as any[]) {
        const id = String(r.variante_id);
        const nombre = r.variantes?.nombre;
        if (!nombre || porId.has(id)) continue;
        if (marcasDelRubro) {
          const marca = r.brand_id == null ? null : String(r.brand_id);
          // Sin marca se deja pasar: es un producto de la empresa y no de un rubro, y
          // esconderlo lo dejaría inalcanzable desde acá — el mismo criterio con el que
          // Creaciones conserva los productos sin marca en su filtro.
          if (marca !== null && !marcasDelRubro.has(marca)) continue;
        }
        porId.set(id, String(nombre));
      }
      setProductos(
        [...porId.entries()]
          .map(([id, nombre]) => ({ id, nombre }))
          .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, conFiltroProducto, rubro, marcas]);

  // Cerrar el desplegable al tocar afuera. En `mousedown` como el resto de las capas de
  // la app, para que el toque que lo cierra no dispare además lo que haya debajo.
  useEffect(() => {
    if (!abierto) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (t && filtroRef.current?.contains(t)) return;
      setAbierto(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [abierto]);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setLoading(true);
    // Con rubro, esperar a saber sus marcas en vez de dibujar las creaciones de toda la
    // empresa y sacarlas un frame después: lo que se ve en ese frame es justo lo que este
    // filtro existe para no mostrar. Se queda en el skeleton.
    if (rubro && !marcas) return;
    // Misma razón para la marca: con `brandId === null` todavía no se sabe cuál es, y
    // dibujar las de toda la empresa mientras tanto es justo lo que este filtro evita.
    if (esperandoMarca) return;
    (async () => {
      // Con un producto elegido la consulta se acota a ÉL y no se filtra en JS: el set
      // de un producto tiene que venir completo, y las últimas 60 creaciones de la
      // empresa pueden no tener ninguna suya.
      //
      // Ese producto ya es de una marca sola, así que ahí el rubro no filtra nada: el
      // recorte por rubro sólo tiene sentido sobre "todas las creaciones".
      const acotarA = productoFijo ?? (productoId !== TODOS ? productoId : null);
      // `brandId` es el mismo recorte que el rubro pero a UNA marca, así que cuando viene
      // manda él: filtrar además por el rubro no sacaría nada que este no saque ya.
      const filtrarPorMarca = !acotarA && !!brandId;
      const filtrarPorRubro =
        !acotarA && !filtrarPorMarca && !!marcas?.hayOtras && marcas.ids.size > 0;
      let q = supabase
        .from('imagenes_generadas')
        .select(
          // El filtro entra en la CONSULTA (no en JS) por `brand_id`, columna propia desde
          // ADR-2026-09-13-brand-id-en-imagenes-generadas. Se probó al revés —traer de más
          // y descartar acá— y no alcanza: en una cuenta mixta las últimas 240 creaciones
          // traían 10 muebles, con 108 más atrás que no se veían nunca. Filtrando en la
          // query el tope de 60 se gasta entero en creaciones del rubro.
          'id, file_name, tipo, variante_id, variantes:variantes(nombre)',
        )
        .eq('id_company', companyId)
        .eq('active', true)
        .not('file_name', 'is', null);
      // `productoFijo` manda sobre el desplegable: es el contexto el que acota, no una
      // elección que se pueda deshacer desde acá.
      if (acotarA) q = q.eq('variante_id', acotarA);
      // Lo que NO tiene marca (94k filas históricas sin producto o con producto huérfano)
      // ENTRA en todas las marcas y en todos los rubros (`orDeMarca` suma
      // `brand_id.is.null`, ADR-2026-09-13-las-generaciones-sin-marca-se-ven-en-todas):
      // esconderlo bajo todas hacía creer que se había borrado, y es el mismo criterio
      // que la app aplica a los productos huérfanos. El costo es que en una cuenta mixta
      // esas filas aparecen en un rubro que no es el suyo —autos dentro de muebles—; se
      // acepta porque las nuevas ya traen marca y el conjunto sin marca no crece más.
      if (filtrarPorRubro) {
        q = q.or(orDeMarca([...marcas!.ids]));
      }
      if (filtrarPorMarca) {
        q = q.or(orDeMarca(brandId!));
      }
      const { data, error } = await q
        .order('created_at', { ascending: false })
        .limit(LIMIT);
      if (error || !data) {
        if (!cancelled) {
          setFilas([]);
          setLoading(false);
        }
        return;
      }
      // Los videos no se animan. El filtro va en JS y no en la query porque
      // `tipo` puede venir null en filas viejas y un `neq` en Postgres las
      // descartaría (NULL <> 'video' es NULL, no true). Mismo criterio de
      // detección que el historial: tipo video/video_pro o file_name .mp4.
      const soloImagenes = data.filter(
        (r: any) =>
          r.tipo !== 'video' &&
          r.tipo !== 'video_pro' &&
          !String(r.file_name).toLowerCase().endsWith('.mp4'),
      );
      // Acá NO se firma nada: sólo se guarda lo que hay. Firmar las 60 era lo que
      // dejaba el panel en el esqueleto mientras volvían 60 pedidos de Storage.
      const nuevas: FilaGenerada[] = soloImagenes.map((r: any) => ({
        id: String(r.id),
        path: `${companyId}/full/${r.file_name}`,
        varianteId: r.variante_id == null ? null : String(r.variante_id),
        producto: r.variantes?.nombre ? String(r.variantes.nombre) : null,
        fileName: String(r.file_name),
      }));
      if (!cancelled) {
        setFilas(nuevas);
        // Otra lista es otra primera página: sin esto, cambiar de producto arrastraba el
        // scroll inflado del filtro anterior y pedía decenas de firmas de una.
        setVisibles(CELDAS_POR_PAGINA);
        // Y otra lista son otros archivos: las que fallaron antes no dicen nada de éstos,
        // y arrastrarlas escondería filas que sí se pueden firmar.
        setFallidas(new Set());
        setReintentos(0);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, productoId, productoFijo, rubro, marcas, brandId, esperandoMarca]);


  // ── El filtro por producto ─────────────────────────────────────────────────
  // Las creaciones de una empresa se leen como CARPETAS por producto, igual que en
  // Creaciones, y desde acá se puede partir de todas las de una
  // (ADR-2026-09-04-elegir-el-set-de-un-producto).
  const productoElegido = productos.find((p) => p.id === productoId) ?? null;
  const productosVisibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return productos;
    return productos.filter((p) => p.nombre.toLowerCase().includes(q));
  }, [productos, busqueda]);

  /**
   * Lo que se puede elegir: las filas menos las que Storage no pudo firmar. Es la lista
   * contra la que cuentan el botón de "Elegir las N", las páginas y el centinela.
   */
  const filasUtiles = useMemo(
    () => filas.filter((f) => !fallidas.has(f.path)),
    [filas, fallidas],
  );

  // ── Firmar de a una página ─────────────────────────────────────────────────
  /**
   * Firma las celdas que se están por dibujar, EN UN pedido (`firmarEnLote`), y deja el
   * resto para cuando el usuario baje. Lo que ya está en la caché de firmas ni se pide,
   * así que volver a abrir el panel no sale a la red.
   */
  const aFirmar = useMemo(
    () => filasUtiles.slice(0, visibles).map((f) => f.path),
    [filasUtiles, visibles],
  );
  useEffect(() => {
    const pendientes = aFirmar.filter((path) => !urls.has(path) && !fallidas.has(path));
    if (pendientes.length === 0) return;
    let cancelado = false;
    // El id del reintento vive ACÁ, en el scope del efecto, y no adentro del `async`: lo
    // que devuelve una función async es su valor resuelto, no la limpieza del efecto, así
    // que un `return () => clearTimeout(t)` de adentro no lo cancela nunca. Sin esto, el
    // temporizador de una lista seguía vivo después de cambiar de producto y le gastaba un
    // reintento a la lista nueva.
    let reintento: number | undefined;
    (async () => {
      const mapa = await firmarEnLote(pendientes, GENERADAS_BUCKET);
      if (cancelado) return;
      if (mapa.size === 0) {
        // NINGUNA volvió: casi seguro se cayó el pedido, no que las doce estén muertas.
        // Se reintenta un par de veces antes de darlas por perdidas; si igual no vuelven,
        // el que scrollea o reabre el panel dispara el efecto de nuevo.
        if (reintentos < MAX_REINTENTOS_FIRMA) {
          reintento = window.setTimeout(() => {
            if (!cancelado) setReintentos((n) => n + 1);
          }, ESPERA_REINTENTO_MS);
        }
        return;
      }
      // El servidor contestó: los paths que NO están en lo que volvió son los que de
      // verdad no existen. Se marcan para no volver a pedirlos ni dejar su celda
      // cargando.
      const noVolvieron = pendientes.filter((path) => !mapa.has(path));
      if (noVolvieron.length > 0) {
        setFallidas((prev) => new Set([...prev, ...noVolvieron]));
      }
      setUrls((prev) => {
        const out = new Map(prev);
        for (const [path, url] of mapa) out.set(path, url);
        return out;
      });
      // Un lote que vuelve bien repone el presupuesto: el tope es de fallas SEGUIDAS, no
      // de la lista entera. Si no, la primera página se comía los dos reintentos y la
      // segunda se quedaba sin ninguno ante una caída pasajera.
      setReintentos(0);
    })();
    return () => {
      cancelado = true;
      if (reintento !== undefined) window.clearTimeout(reintento);
    };
  }, [aFirmar, urls, fallidas, reintentos]);

  /** La fila, ya con su URL, que es lo que ve quien usa la grilla. */
  const pickDe = (f: FilaGenerada, signedUrl: string): GeneradaPick => ({
    id: f.id,
    signedUrl,
    varianteId: f.varianteId,
    producto: f.producto,
    fileName: f.fileName,
  });

  /**
   * Las celdas dibujadas: la página actual, y de ésa sólo las que ya tienen firma. Una
   * celda sin firmar todavía no se dibuja a medias —quedaría un cuadro gris sin foto—:
   * entra cuando su URL llegó, que es lo que hace que la grilla se llene de a tramos.
   */
  const visiblesConUrl = useMemo(() => {
    const out: GeneradaPick[] = [];
    for (const f of filasUtiles.slice(0, visibles)) {
      const url = urls.get(f.path);
      if (url) out.push(pickDe(f, url));
    }
    return out;
  }, [filasUtiles, visibles, urls]);

  /** Queda lista por dibujar: es lo que mantiene vivo el centinela. */
  const quedaPorMostrar = visibles < filasUtiles.length;
  /**
   * Celdas de la página actual que todavía esperan su firma. Las que ya se sabe que NO
   * van a firmar salieron de `filasUtiles`, así que no cuentan: si contaran, su esqueleto
   * no se iría nunca.
   */
  const faltanEnLaPagina =
    Math.min(visibles, filasUtiles.length) - visiblesConUrl.length;

  /**
   * El centinela del final, medido por GEOMETRÍA y no con un `IntersectionObserver` —
   * mismo motivo que en la columna de resultados
   * (ADR-2026-09-04-el-centinela-se-mide-por-geometria): esta grilla vive adentro de un
   * panel que scrollea por su cuenta, y con un `root` equivocado el observer no
   * intersecta nunca y la lista queda clavada. El `true` del listener es lo que hace que
   * el scroll de un contenedor anidado —que no burbujea— llegue igual.
   */
  const centinelaRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!quedaPorMostrar) return;
    const chequear = () => {
      const el = centinelaRef.current;
      if (!el) return;
      // 400px antes de llegar, así el recorrido no se frena contra una grilla que no creció.
      if (el.getBoundingClientRect().top <= window.innerHeight + 400) {
        setVisibles((n) => n + CELDAS_POR_PAGINA);
      }
    };
    chequear();
    window.addEventListener('scroll', chequear, true);
    window.addEventListener('resize', chequear);
    return () => {
      window.removeEventListener('scroll', chequear, true);
      window.removeEventListener('resize', chequear);
    };
  }, [quedaPorMostrar]);

  const elegidas = selectedIds ?? (selectedId ? [selectedId] : []);
  const todasElegidas =
    filasUtiles.length > 0 && filasUtiles.every((r) => elegidas.includes(r.id));

  /**
   * La lista que se está mirando AHORA. `elegirTodas` espera a Storage, y en esa espera el
   * usuario puede cambiar de producto o cerrar el panel: sin este chequeo, la respuesta
   * vieja terminaba agregando (o sacando) fotos del producto ANTERIOR.
   */
  const filasRef = useRef(filas);
  filasRef.current = filas;

  /**
   * "Elegir las N" se lleva el SET ENTERO, incluso lo que todavía no se dibujó, así que
   * acá sí hay que firmar todo — en un solo pedido, y recién cuando se toca el botón.
   */
  const elegirTodas = async () => {
    if (!onElegirVarias) return;
    const lista = filasUtiles;
    // SACAR no necesita firmar nada: quien recibe esto sólo mira el `id` para sacarlas.
    // Antes salía a Storage igual, así que con las firmas caídas el botón no hacía NADA.
    if (todasElegidas) {
      onElegirVarias(
        lista.map((f) => pickDe(f, urls.get(f.path) ?? '')),
        false,
      );
      return;
    }
    // Elegir sí las necesita: el set entero, incluso lo que no se dibujó todavía. Va en
    // un solo pedido y recién al tocar el botón.
    const mapa = await firmarEnLote(
      lista.map((f) => f.path),
      GENERADAS_BUCKET,
    );
    // Se cambió de producto (o se cerró el panel) mientras viajaba el pedido: lo que
    // vuelve es de otra lista y aplicarlo sería mover una selección que ya no se ve.
    if (filasRef.current !== filas) return;
    // Mismo criterio que arriba: un lote entero vacío es el pedido caído, no doce
    // archivos muertos, y marcarlos los sacaría de la lista.
    if (mapa.size > 0) {
      const noVolvieron = lista.flatMap((f) => (mapa.has(f.path) ? [] : [f.path]));
      if (noVolvieron.length > 0) {
        setFallidas((prev) => new Set([...prev, ...noVolvieron]));
      }
    }
    setUrls((prev) => {
      const out = new Map(prev);
      for (const [path, url] of mapa) out.set(path, url);
      return out;
    });
    const todas = lista.flatMap((f) => {
      const url = mapa.get(f.path) ?? urls.get(f.path);
      return url ? [pickDe(f, url)] : [];
    });
    if (todas.length > 0) onElegirVarias(todas, true);
  };

  const elegirProducto = (id: string) => {
    setProductoId(id);
    setAbierto(false);
    setBusqueda('');
  };

  const filtro = conFiltroProducto && !productoFijo ? (
    <div className="flex items-center gap-2 mb-3">
      <div className="relative" ref={filtroRef}>
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          className={`h-9 pl-3.5 pr-2.5 rounded-full inline-flex items-center gap-1.5 text-[13.5px] font-semibold transition-colors ${
            productoElegido
              ? 'bg-brand-primary text-white'
              : 'bg-black/[0.045] text-brand-black hover:bg-black/[0.07]'
          }`}
        >
          <span className="max-w-[180px] truncate">
            {productoElegido?.nombre ?? 'Todos los productos'}
          </span>
          <ChevronDown
            className={`w-4 h-4 shrink-0 transition-transform ${abierto ? 'rotate-180' : ''}`}
          />
        </button>
        {abierto && (
          // Absoluto y no un portal: la grilla vive adentro de un panel que ya scrollea,
          // y colgarlo del `body` lo dejaba flotando cuando ese panel se movía.
          <div className="absolute z-20 mt-1.5 left-0 w-[260px] rounded-2xl bg-white border border-black/[0.06] shadow-[0_12px_32px_rgba(0,0,0,0.14)] overflow-hidden">
            {/* El catálogo puede tener decenas de productos: el buscador va adentro del
                desplegable, como en Creaciones. */}
            <div className="p-2 border-b border-black/[0.06]">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-black/35" />
                <input
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar producto..."
                  autoFocus
                  className="w-full h-9 pl-8 pr-3 rounded-xl bg-black/[0.035] text-[13.5px] text-brand-black placeholder:text-black/35 focus:outline-none focus:bg-black/[0.055] transition-colors"
                />
              </div>
            </div>
            <div className="max-h-[240px] overflow-y-auto py-1">
              <button
                type="button"
                onClick={() => elegirProducto(TODOS)}
                className="w-full px-3.5 py-2 flex items-center justify-between gap-2 text-left text-[13.5px] text-brand-black hover:bg-black/[0.04] transition-colors"
              >
                Todos los productos
                {productoId === TODOS && (
                  <Check className="w-4 h-4 shrink-0 text-brand-primary" />
                )}
              </button>
              {productosVisibles.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => elegirProducto(p.id)}
                  className="w-full px-3.5 py-2 flex items-center justify-between gap-2 text-left text-[13.5px] text-brand-black hover:bg-black/[0.04] transition-colors"
                >
                  <span className="min-w-0 truncate">{p.nombre}</span>
                  {productoId === p.id && (
                    <Check className="w-4 h-4 shrink-0 text-brand-primary" />
                  )}
                </button>
              ))}
              {productosVisibles.length === 0 && (
                <p className="px-3.5 py-3 text-[13px] text-black/45">
                  Ningún producto con ese nombre.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Elegir el SET entero. Aparece con un producto filtrado y algo que elegir: es lo
          que separa "elegí estas tres fotos" de "quiero todas las de este mueble". */}
      {productoElegido && onElegirVarias && filasUtiles.length > 0 && (
        <button
          type="button"
          onClick={elegirTodas}
          className="h-9 px-3.5 rounded-full text-[13.5px] font-semibold text-brand-primary hover:bg-brand-primary/[0.08] transition-colors whitespace-nowrap"
        >
          {todasElegidas ? 'Quitar las ' : 'Elegir las '}
          {filasUtiles.length}
        </button>
      )}
    </div>
  ) : null;

  const vacio = (
    <div className="text-center text-[14px] text-[#3C3C43]/55 py-12 px-6">
      {emptyHint ??
        'Todavía no generaste ninguna imagen. Subí una foto para animarla.'}
    </div>
  );

  // Con `tileInicial`, el skeleton y el vacío NO reemplazan a la grilla: las celdas propias
  // —la de subir, sobre todo— tienen que seguir estando mientras carga y cuando no hay
  // ninguna creación, que es justo cuando subir es la única salida.
  const cuerpo = (() => {
    if (loading || esperandoMarca) {
      return tileInicial ? (
        <div className={GRID_CLASS}>
          {tileInicial}
          <ProductGridSkeleton count={9} tileClassName="rounded-[13px]" bare />
        </div>
      ) : (
        <ProductGridSkeleton
          count={10}
          gridClassName={GRID_CLASS}
          tileClassName="rounded-[13px]"
        />
      );
    }

    if (filasUtiles.length === 0) {
      return tileInicial ? (
        <>
          <div className={GRID_CLASS}>{tileInicial}</div>
          {vacio}
        </>
      ) : (
        vacio
      );
    }

    return (
      <div className={GRID_CLASS}>
        {tileInicial}
        {visiblesConUrl.map((r) => {
          const isSel = selectedIds ? selectedIds.includes(r.id) : selectedId === r.id;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => onSelect(r)}
              className="group relative aspect-square rounded-[13px] overflow-hidden bg-neutral-100 transition-all active:scale-[0.98]"
            >
              <CachedImg
                src={r.signedUrl}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
              />
              {/* El borde va en una CAPA APARTE, encima de la foto, y no en el `className`
                  del botón. Por fuera del tile lo recortaba el scroller de la grilla
                  —`overflow-y-auto`, y en CSS un eje distinto de `visible` deja el otro en
                  `auto`, así que recorta también en X—, y los 2px del anillo caían justo
                  afuera en las columnas de los bordes. Con `ring-inset` a secas tampoco se
                  veía: un `box-shadow` interior se pinta DEBAJO de los hijos, y la foto lo
                  tapaba entero. Esta capa es hija, así que va arriba de la foto y adentro
                  del tile. */}
              <span
                aria-hidden="true"
                className={`pointer-events-none absolute inset-0 rounded-[13px] ring-inset transition-shadow ${
                  isSel
                    ? 'ring-2 ring-brand-primary'
                    : 'ring-1 ring-black/[0.06] group-hover:ring-brand-primary/40'
                }`}
              />
              {isSel && (
                <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-primary text-white shadow-sm">
                  <Check className="w-3.5 h-3.5" />
                </span>
              )}
            </button>
          );
        })}
        {/* Los huecos de la página que se está firmando: mismas celdas del esqueleto, en
            la misma grilla, así la fila no se corta en seco mientras llegan las firmas. */}
        {faltanEnLaPagina > 0 && (
          <ProductGridSkeleton
            count={faltanEnLaPagina}
            tileClassName="rounded-[13px]"
            bare
          />
        )}
        {/* El centinela: última celda de la grilla, sin alto propio. Cuando queda a la
            vista entra la página siguiente. */}
        {quedaPorMostrar && <div ref={centinelaRef} aria-hidden="true" />}
      </div>
    );
  })();

  return (
    <>
      {filtro}
      {cuerpo}
    </>
  );

};

export default GeneradasPickGrid;
